/**
 * @voltxampere/schema — SimulationSettings
 *
 * Per SPECS §1.6. Root settings object with DC, Transient, and AC
 * sub-objects. SPECS-mandated defaults:
 *   dc.temperature = 27, gmin = 1e-12, reltol = 1e-3, abstol = 1e-12,
 *                    maxIterations = 100
 *   transient.tstart = 0, tstop = 1e-3, method = "trap",
 *                        reltol = 1e-3, abstol = 1e-12
 *   ac.start = 1, stop = 1e9, points = 100, sweep = "dec"
 *
 * Cross-field invariants (ac.start < ac.stop, transient.tstart <
 * transient.tstop) are enforced via Zod `.refine()`.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
export const AnalysisKindSchema = z.enum(["dc", "transient", "ac", "noise", "monte-carlo"]);
/**
 * Sprint 8c: `.NODESET`-style DC operating-point hints.
 *
 * Mirrors ngspice's `.NODESET V(node)=value` / `.IC` directives.
 * The solver uses these to pre-seed Newton's initial iterate AND
 * pre-populate pnjlim's junction history, so the first iteration
 * starts inside the target basin of attraction rather than at the
 * trivial BJT-off fixed point (V=0 everywhere, KCL trivially
 * satisfied — see `docs/sprints/diagnostics/sprint-8c-pnp-diagnostic.md`
 * Test 3).
 *
 * Hints are tavsiye niteliğinde: Newton still believes the stamps'
 * KCL balance, so a wrong hint is self-corrected by Armijo
 * backtracking on the reduced-KCL residual (Sprint 8c primary tool).
 *
 * `components` keyed by `ComponentInstance.id`. For BJTs, `vbe` /
 * `vbc` are signed junction voltages in the device's convention
 * (NPN: V_B − V_E; PNP: V_E − V_B from the user's perspective, but
 * at the hint layer we treat the magnitude as "active-region
 * forward drop ≈ 0.7 V for silicon"). For MOSFETs, `vgs` / `vds`.
 * For linear components, a direct `voltage` override.
 *
 * `nodes` keyed by net id → voltage hint. Applied only when the
 * net is NOT the ground net (ground is always 0 by construction).
 *
 * Omitted fields default to `undefined` and do not override the
 * zero-start behaviour — callers can specify just the fields they
 * want to seed.
 */
export const DCInitialConditionsSchema = z
    .object({
    components: z
        .record(z.string(), z
        .object({
        /** Base-emitter junction voltage hint (V). BJT only. */
        vbe: z.number().finite().optional(),
        /** Base-collector junction voltage hint (V). BJT only. */
        vbc: z.number().finite().optional(),
        /** Collector-emitter voltage hint (V). BJT only. */
        vce: z.number().finite().optional(),
        /** Gate-source voltage hint (V). MOSFET only. */
        vgs: z.number().finite().optional(),
        /** Drain-source voltage hint (V). MOSFET only. */
        vds: z.number().finite().optional(),
        /** Direct branch voltage override (V). Linear components only. */
        voltage: z.number().finite().optional(),
    })
        .strict())
        .optional(),
    nodes: z.record(z.string(), z.number().finite()).optional(),
})
    .strict();
export const DCSettingsSchema = z
    .object({
    temperature: z.number().default(27),
    gmin: z.number().positive().default(1e-12),
    reltol: z.number().positive().default(1e-3),
    abstol: z.number().positive().default(1e-12),
    maxIterations: z.number().int().positive().default(100),
    /**
     * Sprint 8a: toggle the Newton trust-region step damping
     * introduced in engine-v0.3.2. On by default — the PNP CE
     * limit-cycle case study in SPRINT_8A_REPORT.md justifies the
     * universal-on policy. Turn off only to reproduce pre-8a
     * numerics for a regression test or to isolate a bug.
     */
    enableDeltaVDamping: z.boolean().default(true),
    /**
     * Sprint 8b: two-tier convergence strategy.
     *  - `"strict"` (default): SPECS §3.4 defaults. Reject the
     *    solve if Newton cannot reach `reltol = 1e-3`.
     *  - `"two-tier"`: after a strict failure, retry at the
     *    relaxed band (`reltol = 1e-2`). A relaxed success is
     *    flagged in `DCResult.convergenceTier = "relaxed"` so
     *    downstream consumers can warn the user.
     *
     * Relaxed is an opt-in tier: some hard-to-converge DC OP
     * circuits (PNP saturation-edge biasing) settle 10× above the
     * strict tolerance but still sit at the physically correct
     * operating point. The two-tier path accepts them with a
     * visible warning rather than throwing.
     */
    toleranceStrategy: z.enum(["strict", "two-tier"]).default("strict"),
    /**
     * Sprint 8b: opt-in Anderson-II acceleration fallback. When
     * `true`, the solver ladder becomes Newton → gmin → source →
     * Anderson-II. Default `false` — Sprint 8b ships the
     * infrastructure; Sprint 9 observational validation flips the
     * default after enough workload hits confirm no regression.
     */
    enableAnderson: z.boolean().default(false),
    /**
     * Sprint 8c: `.NODESET`-style DC operating-point hints. See
     * {@link DCInitialConditionsSchema}. Omitting the field keeps
     * the pre-8c cold-start behaviour (x0 = zeros, empty pnjlim
     * history). Supplying hints pre-seeds Newton's initial iterate
     * and is the primary escape from the trivial BJT-off fixed
     * point that defeated Sprint 8a/8b on the PNP CE fixture.
     *
     * The flag is additive (optional) so migration 0.2.0 → 0.3.0
     * does not need to fill it in — old circuits simply omit it and
     * use the Sprint 8a/8b path. New circuits opt in via schematic
     * `.NODESET` annotations (Sprint 10+ UI) or explicit API.
     */
    initialConditions: DCInitialConditionsSchema.optional(),
    /**
     * Sprint 8c: enable Armijo backtracking line-search on the
     * reduced-KCL residual. Default `true` — Armijo only engages
     * when (a) `initialConditions` are supplied, or (b) stall
     * detection fires after ≥3 iterations of <5% residual
     * reduction. Linear circuits (no junction-bearing nets) skip
     * Armijo unconditionally so NR's quadratic convergence is not
     * dragged back to linear.
     *
     * Set `false` to reproduce pre-8c Newton behaviour — used by
     * the Sprint 8a.1 NPN regression (7-iter baseline) to prove
     * Armijo does not engage on well-behaved circuits.
     */
    enableArmijo: z.boolean().default(true),
    /**
     * Sprint 9: settings-level assertion shortcuts. DSL source
     * strings evaluated against the DC operating point at the
     * end of {@link DCSolver.solve}. For richer per-assertion
     * metadata (id, locale, enabled, description), use the
     * top-level `Circuit.assertions` array instead — this slot
     * is a shortcut for the most common "check one thing at the
     * OP" workflow.
     */
    assertions: z.array(z.string().min(1).max(500)).optional(),
})
    .strict();
export const TransientMethodSchema = z.enum(["trap", "gear2", "be"]);
export const TransientSettingsSchema = z
    .object({
    tstart: z.number().default(0),
    tstop: z.number().positive().default(1e-3),
    tstepMax: z.number().positive().optional(),
    method: TransientMethodSchema.default("trap"),
    reltol: z.number().positive().default(1e-3),
    abstol: z.number().positive().default(1e-12),
    /**
     * Sprint 9: settings-level transient assertion shortcuts.
     * DSL source strings evaluated against the last transient
     * sample. `all times` / `tüm zamanlar`-conditioned assertions
     * inside this list exercise {@link computeBuiltinTransient}
     * across the full sample series (peak / RMS).
     */
    assertions: z.array(z.string().min(1).max(500)).optional(),
})
    .strict()
    .refine((s) => s.tstart < s.tstop, {
    message: "transient.tstart must be strictly less than transient.tstop",
    path: ["tstop"],
});
export const ACSweepSchema = z.enum(["lin", "dec", "oct"]);
export const ACSettingsSchema = z
    .object({
    start: z.number().positive().default(1),
    stop: z.number().positive().default(1e9),
    points: z.number().int().positive().default(100),
    sweep: ACSweepSchema.default("dec"),
})
    .strict()
    .refine((s) => s.start < s.stop, {
    message: "ac.start must be strictly less than ac.stop",
    path: ["stop"],
});
export const SimulationSettingsSchema = z
    .object({
    defaultAnalysis: AnalysisKindSchema.default("dc"),
    dc: DCSettingsSchema.default({}),
    transient: TransientSettingsSchema.default({
        tstart: 0,
        tstop: 1e-3,
        method: "trap",
        reltol: 1e-3,
        abstol: 1e-12,
    }),
    ac: ACSettingsSchema.default({
        start: 1,
        stop: 1e9,
        points: 100,
        sweep: "dec",
    }),
})
    .strict();
//# sourceMappingURL=simulation-settings.js.map